'use client';
import React from 'react';

import Switch from '@mui/material/Switch';
import { usePathname } from 'next/navigation';
import styled from 'styled-components';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import persistentState from '@/lib/persistentState';
import { log } from '@/utils/logger';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
`;

const BlinkingText = styled.span<{ isPreviewModeActive: boolean }>`
  animation: ${({ isPreviewModeActive }) => (isPreviewModeActive ? 'blink 2s infinite' : 'none')};

  @keyframes blink {
    0% {
      opacity: 0.5;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.5;
    }
  }
`;

const PREVIEW_ENABLED_PATHS = ['/product', '/category', '/article', '/guide', '/manual'];

const isPathEnabled = (pathname: string) => {
  if (!pathname) {
    return false;
  }

  if (pathname === '/') {
    return true;
  }

  return PREVIEW_ENABLED_PATHS.some(path => pathname.startsWith(path));
};

export default function PreviewModeSwitch() {
  const [switchOn, setSwitchOn] = React.useState(false);
  const pathname = usePathname();
  const breakpoint = useBreakpoint();

  const isPreviewModeActive = React.useMemo(() => {
    const previewModeCookie = persistentState.cookie.get({
      key: 'preview-mode',
    });

    if (!previewModeCookie) {
      return false;
    }

    // Handle both string 'true' and boolean true for consistency
    const isActive = previewModeCookie === true || previewModeCookie === 'true';

    // Debug logging for troubleshooting
    log.debug({
      event: 'preview_mode_cookie_check',
      message: 'Checking preview mode cookie state',
      labels: {
        component: 'PreviewModeSwitch',
        pathname,
      },
      stringify: {
        cookieValue: previewModeCookie,
        cookieType: typeof previewModeCookie,
        isActive,
      },
    });

    return isActive;
  }, [pathname]);

  const handleSwitchChange = React.useCallback(() => {
    // Prevent multiple clicks during transition
    if (document.body.style.opacity === '0.5') return;

    // Debug logging for troubleshooting
    log.debug({
      event: 'preview_mode_switch_triggered',
      message: 'User clicked preview mode switch',
      labels: {
        component: 'PreviewModeSwitch',
        pathname,
      },
      stringify: {
        isPreviewModeActive,
        switchOn,
        action: isPreviewModeActive ? 'exit' : 'enter',
      },
    });

    // make page opacity 0.5
    document.body.style.opacity = '0.5';

    if (isPreviewModeActive) {
      // Immediately update local state to reflect the change
      setSwitchOn(false);
      log.info({
        event: 'preview_mode_exit',
        message: 'Exiting preview mode',
        labels: {
          component: 'PreviewModeSwitch',
          pathname,
        },
      });
      // Use setTimeout to ensure state update is processed before redirect
      setTimeout(() => {
        window.location.href = '/edge/preview?exit=true&redirect=' + window.location.pathname;
      }, 0);
    } else {
      // Immediately update local state to reflect the change
      setSwitchOn(true);
      log.info({
        event: 'preview_mode_enter',
        message: 'Entering preview mode',
        labels: {
          component: 'PreviewModeSwitch',
          pathname,
        },
      });
      // Use setTimeout to ensure state update is processed before redirect
      setTimeout(() => {
        window.location.href = '/edge/preview?redirect=' + window.location.pathname;
      }, 0);
    }
  }, [isPreviewModeActive, switchOn]);

  // React.useEffect(() => {
  //   if (persistentState.session) {
  //     if (isPreviewModeActive) {
  //       persistentState.session.set({
  //         key: PERSISTENT_STATE_KEYS.previewMode,
  //         value: true,
  //       });
  //     } else {
  //       persistentState.session.delete({
  //         key: PERSISTENT_STATE_KEYS.previewMode,
  //       });
  //     }
  //   }
  // }, [isPreviewModeActive]);

  const label = React.useMemo(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 500) {
      return '';
    }

    return breakpoint === 'xs' ? 'Preview' : 'Preview Mode';
  }, [breakpoint]);

  if (!isPathEnabled(pathname)) {
    return null;
  }

  return (
    <Wrapper>
      <BlinkingText isPreviewModeActive={isPreviewModeActive}>{label}</BlinkingText>
      <Switch checked={isPreviewModeActive || switchOn} onChange={handleSwitchChange} color="primary" />
      {/* <span>{isPreviewMode ? 'Preview Mode ON' : 'Preview Mode OFF'}</span> */}
    </Wrapper>
  );
}
